import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Copy, RefreshCw, Trash2, Loader2, Link as LinkIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuddySettingsCard() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/buddy/link`, { withCredentials: true });
        if (!cancelled) setToken(r.data?.token || null);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/buddy/link`, {}, { withCredentials: true });
      setToken(r.data.token);
      toast.success(token ? 'Länk uppdaterad ✨' : 'Länk skapad ✨');
    } catch { toast.error('Kunde inte skapa länk'); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Vill du återkalla länken? Den kommer sluta fungera direkt.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/buddy/link`, { withCredentials: true });
      setToken(null);
      toast.success('Länken återkallad');
    } catch { toast.error('Kunde inte återkalla'); }
    finally { setBusy(false); }
  };

  const url = token ? `${window.location.origin}/buddy/${token}` : '';

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('📋 Länk kopierad!');
    } catch { toast.error('Kunde inte kopiera'); }
  };

  return (
    <section className="neo-card p-4 bg-gradient-to-br from-[#FFE4EC] via-[#F5EEFF] to-[#E8F4FF]" data-testid="buddy-settings-card">
      <header className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-60">Accountability</p>
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><Heart className="w-5 h-5 text-[#D06892]" /> Buddy-länk</h3>
        </div>
      </header>

      <p className="text-xs opacity-70 mb-3">
        Dela en skrivskyddad länk med en kompis så hen kan se din streak, dina poäng och tasks senaste veckan — utan att behöva ett konto.
      </p>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin opacity-60" /></div>
      ) : token ? (
        <div className="space-y-2">
          <div className="neo-card p-2 bg-white flex items-center gap-2" data-testid="buddy-link-display">
            <LinkIcon className="w-4 h-4 opacity-60 shrink-0" />
            <code className="text-[11px] truncate flex-1" title={url}>{url}</code>
            <button onClick={copy} className="neo-btn p-1.5 bg-[#FFC8DD]" title="Kopiera" data-testid="buddy-copy">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={create}
              disabled={busy}
              className="neo-btn flex-1 py-1.5 text-xs font-bold bg-[#FFE8A3]"
              data-testid="buddy-rotate"
              title="Skapar en helt ny länk — gamla slutar fungera"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : <><RefreshCw className="w-3 h-3 inline mr-1" /> Byt länk</>}
            </button>
            <button
              onClick={revoke}
              disabled={busy}
              className="neo-btn px-3 py-1.5 text-xs font-bold bg-red-100 text-red-700"
              data-testid="buddy-revoke"
            >
              <Trash2 className="w-3 h-3 inline mr-1" /> Återkalla
            </button>
          </div>
          <p className="text-[10px] opacity-60 italic">⚠️ Låt bara personer du litar på se länken.</p>
        </div>
      ) : (
        <button
          onClick={create}
          disabled={busy}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold"
          data-testid="buddy-create"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : '✨ Skapa Buddy-länk'}
        </button>
      )}
    </section>
  );
}
